/*
 * Thunderstorm is a full web app framework!
 *
 * Typescript & Express backend infrastructure that natively runs on firebase function
 * Typescript & React frontend infrastructure
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __stringify, _keys, asArray, BadImplementationException, exists, isErrorOfType, MimeType_json, Minute, ModuleManager } from '@nu-art/ts-common';
import { HttpMethod } from './types.js';
import { HttpException } from './request-types.js';
import { dispatcher_onAuthRequired } from './no-auth-listener.js';
import { DefaultHttpServerConfig } from './consts.js';
import { HttpCodes } from '@nu-art/ts-common/core/exceptions/http-codes';
export class BaseHttpRequest {
    key;
    requestData;
    origin;
    headers = {};
    method = HttpMethod.GET;
    timeout = 10000;
    body;
    url;
    params = {};
    responseType;
    label;
    onProgressListener;
    aborted = false;
    compress;
    logger;
    onCompleted;
    onError;
    constructor(requestKey, requestData) {
        this.key = requestKey;
        this.requestData = requestData;
        this.label = `http request: ${requestKey}${requestData ? ` ${requestData}` : ''}`;
        this.compress = false;
        // @ts-ignore
        if (ModuleManager.instance.config.isDebug)
            this.timeout = 5 * Minute;
    }
    resolveTypedException(exception) {
        if (isErrorOfType(exception, HttpException))
            return exception.errorResponse?.error;
    }
    setLogger(logger) {
        this.logger = logger;
        return this;
    }
    getRequestData() {
        return this.requestData;
    }
    setOrigin(origin) {
        this.origin = origin;
        return this;
    }
    setOnProgressListener(onProgressListener) {
        this.onProgressListener = onProgressListener;
        return this;
    }
    setLabel(label) {
        this.label = label;
        return this;
    }
    setMethod(method) {
        this.method = method;
        return this;
    }
    setResponseType(responseType) {
        this.responseType = responseType;
        return this;
    }
    setUrlParams(params) {
        if (!params)
            return this;
        _keys(params).forEach((key) => {
            const param = params[key];
            return param && typeof param === 'string' && this.setUrlParam(key, param);
        });
        return this;
    }
    setUrlParam(key, value) {
        delete this.params[key];
        this.params[key] = value;
        return this;
    }
    setUrl(url) {
        this.url = url;
        return this;
    }
    getUrl() {
        return this.url;
    }
    setRelativeUrl(relativeUrl) {
        if (!this.origin)
            throw new BadImplementationException('if you want to use relative urls, you need to set an origin');
        if (relativeUrl.startsWith('/'))
            relativeUrl = relativeUrl.substring(1);
        this.url = `${this.origin}/${relativeUrl}`;
        return this;
    }
    setTimeout(timeout) {
        // console.log(`${this.url} - setting timeout: ${timeout}`);
        this.timeout = timeout;
        return this;
    }
    setHeaders(headers) {
        if (!headers)
            return this;
        Object.keys(headers).forEach((key) => this.setHeader(key, headers[key]));
        return this;
    }
    addHeaders(headers) {
        if (!headers)
            return this;
        Object.keys(headers).forEach((key) => this.addHeader(key, headers[key]));
        return this;
    }
    setHeader(_key, value) {
        const key = _key.toLowerCase();
        delete this.headers[key];
        return this.addHeader(key, value);
    }
    addHeader(_key, value) {
        const key = _key.toLowerCase();
        return this._addHeaderImpl(key, value);
    }
    removeHeader(key) {
        delete this.headers[key];
        return this;
    }
    _addHeaderImpl(key, value) {
        const values = asArray(value);
        if (!this.headers[key])
            this.headers[key] = values;
        else
            this.headers[key].push(...values);
        return this;
    }
    prepareJsonBody(bodyObject) {
        return bodyObject;
    }
    setBodyAsJson(bodyObject, compress) {
        this.setHeader('content-type', MimeType_json);
        this.setBody(this.prepareJsonBody(bodyObject), compress);
        return this;
    }
    setBody(bodyAsString, _compress) {
        this.body = bodyAsString;
        this.compress = _compress === undefined ? this.compress : _compress;
        if (typeof bodyAsString === 'string' && this.compress)
            this.setHeader('Content-encoding', 'gzip');
        return this;
    }
    isValidStatus(statusCode) {
        return statusCode >= 200 && statusCode < 300;
    }
    print() {
        this.logger?.logWarning(`Url: ${this.url}`);
        this.logger?.logWarning(`Params:`, this.params);
        this.logger?.logWarning(`Headers:`, this.headers);
    }
    async executeSync(print = false) {
        if (print)
            this.print();
        await this.executeImpl();
        const status = this.getStatus();
        const requestData = this.body || this.params;
        if (this.aborted) {
            const httpException = new HttpException(status, this.url); // should be status 0
            await this.onError?.(httpException, requestData, this);
            throw httpException;
        }
        if (!this.isValidStatus(status)) {
            const errorResponse = this.getErrorResponse();
            const httpException = new HttpException(status, this.url, errorResponse);
            if (status === HttpCodes._4XX.UNAUTHORIZED.code)
                await dispatcher_onAuthRequired.dispatchModuleAsync(this);
            await this.onError?.(httpException, requestData, this);
            throw httpException;
        }
        let response = this.getResponse();
        if (!exists(response)) {
            await this.onCompleted?.(response, requestData, this);
            return response;
        }
        try {
            response = JSON.parse(response);
        }
        catch (ignore) {
            //
        }
        await this.onCompleted?.(response, requestData, this);
        return response;
    }
    execute(onSuccess = () => this.logger?.logVerbose(`Completed: ${this.label}`), onError = reason => this.logger?.logWarning(`Error: ${this.label}`, reason)) {
        this.executeSync()
            .then(onSuccess)
            .catch(onError);
        return this;
    }
    clearOnCompleted = () => {
        delete this.onCompleted;
    };
    setOnCompleted = (onCompleted) => {
        if (!onCompleted)
            return this;
        if (this.onCompleted && onCompleted) {
            const _onCompleted = this.onCompleted;
            this.onCompleted = async (response, input, request) => {
                await _onCompleted(response, input, request);
                await onCompleted(response, input, request);
            };
        }
        else
            this.onCompleted = async (response, input, request) => {
                await onCompleted?.(response, input, request);
            };
        return this;
    };
    setOnError(onError) {
        if (!onError)
            return this;
        if (this.onError && onError) {
            const _onError = this.onError;
            this.onError = async (response, input, request) => {
                await _onError(response, input, request);
                await onError(response, input, request);
            };
        }
        else
            this.onError = async (response, input, request) => {
                await onError?.(response, input, request);
            };
        return this;
    }
    getResponseHeader(headerKey) {
        try {
            return this._getResponseHeader(headerKey);
        }
        catch (e) {
            this.logger?.logError(`Need to add responseHeaders to backend config: ${__stringify(DefaultHttpServerConfig, true)}`, e);
        }
    }
    abort() {
        this.aborted = true;
        this.abortImpl();
    }
}
//# sourceMappingURL=BaseHttpRequest.js.map